/**
 * Notifications Hooks - Sprint 19b
 *
 * React Query hooks for notifications.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as notificationsService from '../services/notifications.service';

import type { NotificationsQueryParams } from '@bstock/shared';

/**
 * Query key factory for notifications
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (params: NotificationsQueryParams) =>
    [...notificationKeys.lists(), params] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

/**
 * Hook to fetch paginated notifications
 */
export function useNotifications(params: NotificationsQueryParams = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsService.getNotifications(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch notifications with infinite loading
 */
export function useInfiniteNotifications(limit: number = 15) {
  return useInfiniteQuery({
    queryKey: [...notificationKeys.lists(), 'infinite', limit],
    queryFn: ({ pageParam = 1 }) =>
      notificationsService.getNotifications({ page: pageParam, limit }),
    getNextPageParam: (lastPage) => {
      const { page, limit, total } = lastPage.pagination;
      const hasMore = page * limit < total;
      return hasMore ? page + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch unread notifications count
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: notificationsService.getUnreadCount,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute as fallback
  });
}

/**
 * Hook to mark a notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsService.markAsRead,
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: () => {
      toast.error('Erreur lors du marquage comme lu');
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsService.markAllAsRead,
    onSuccess: (result) => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      if (result.count > 0) {
        toast.success(`${result.count} notification(s) marquee(s) comme lue(s)`);
      }
    },
    onError: () => {
      toast.error('Erreur lors du marquage comme lu');
    },
  });
}
